import { ChangeDetectionStrategy, Component, computed, inject, input, model, ViewEncapsulation } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Workflow } from "../service/Workflow";
import { S25Util } from "../../../util/s25-util";
import { S25RichTextEditorModule } from "../../s25-rich-text-editor/s25.rich.text.editor.module";
import { AnswerType } from "../../data-ingestion/data.ingestion.service";
import type { FieldType } from "../../data-ingestion/data.ingestion.const";
import { WorkflowUtil } from "../workflow.util";
import { WorkflowFormContext } from "./workflow.form.context";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-action-input",
    template: `
        <label [for]="inputId()">
            {{ label() }}
            @if (required()) {
                <span class="required">*</span>
            }
        </label>
        @if (help()) {
            <small class="form-text">{{ help() }}</small>
        }

        @switch (type()) {
            @case ("checkbox") {
                <div class="checkbox-wrapper">
                    <input
                        type="checkbox"
                        [checked]="isChecked()"
                        (change)="onCheckboxChange($event)"
                        class="form-check-input"
                        [id]="inputId()"
                    />
                </div>
            }
            @case ("textarea") {
                <textarea
                    [(ngModel)]="value"
                    class="form-control"
                    rows="3"
                    [placeholder]="placeholder()"
                    [id]="inputId()"
                ></textarea>
            }
            @case ("richtext") {
                <s25-ng-rich-text-editor [(modelValue)]="value"></s25-ng-rich-text-editor>
            }
            @case ("text") {
                <input
                    type="text"
                    [(ngModel)]="value"
                    class="form-control"
                    [placeholder]="placeholder()"
                    [id]="inputId()"
                />
            }
        }
        @if (acceptedAnswerTypes()?.length) {
            <small class="accepted-types"
                >Accepts answer types: {{ acceptedAnswerTypes()!.map(answerTypeLabel).join(", ") }}</small
            >
        }
        @if (acceptedFieldTypes()?.length) {
            <small class="accepted-types"
                >Accepts field types: {{ acceptedFieldTypes()!.map(fieldTypeLabel).join(", ") }}</small
            >
        }
        @for (error of validationErrors(); track $index) {
            <div [class]="error.startsWith('Field reference') ? 'validation-warning' : 'validation-error'">
                <i class="fa fa-exclamation-circle"></i> {{ error }}
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
        }

        label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: var(--color-text-secondary, #555);
        }

        label[for] {
            cursor: pointer;
        }

        .required {
            color: var(--color-error-text, #dc3545);
            font-weight: bold;
        }

        .checkbox-wrapper {
            margin-top: 4px;
        }

        .form-check-input {
            margin: 0;
            cursor: pointer;
            width: 18px;
            height: 18px;
            border: 2px solid var(--color-border, #6b7280);
            border-radius: 3px;
            outline: none;
            appearance: auto;
        }

        .form-check-input:focus {
            border-color: var(--color-primary, #3b82f6);
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
        }

        ::ng-deep s25-ng-rich-text-editor .tox.tox-tinymce {
            border-radius: var(--bs-border-radius);
        }
        .validation-error {
            color: var(--color-error-text, #dc3545);
            font-size: 12px;
            margin-top: 4px;
            padding: 4px 8px;
            background: var(--color-error-bg, #f8d7da);
            border: 1px solid var(--color-error-border, #f5c6cb);
            border-radius: 4px;
        }

        .validation-warning {
            color: var(--color-warning-text, #856404);
            font-size: 12px;
            margin-top: 4px;
            padding: 4px 8px;
            background: var(--color-warning-bg, #fff3cd);
            border: 1px solid var(--color-warning-border, #ffeaa7);
            border-radius: 4px;
        }

        .accepted-types {
            color: var(--color-text-muted, #666);
            font-size: 11px;
            margin-top: 2px;
            display: block;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ReactiveFormsModule, FormsModule, S25RichTextEditorModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowActionInputComponent {
    value = model.required<string>();
    label = input.required<Workflow.SystemActionInput["label"]>();
    type = input.required<Workflow.SystemActionInput["type"]>();
    required = input.required<Workflow.SystemActionInput["required"]>();
    help = input<string>("");

    readonly placeholder = computed(() => "Enter " + this.label().toLowerCase());

    private readonly formCtx = inject(WorkflowFormContext, { optional: true });
    readonly acceptedAnswerTypes = input<AnswerType[]>();
    readonly acceptedFieldTypes = input<FieldType[]>();

    readonly inputId = computed(() => {
        const safeId = this.label()
            .replace(/[^a-zA-Z0-9]/g, "-")
            .toLowerCase();
        return `workflow-input-${safeId}`;
    });

    readonly isChecked = computed(() => {
        if (this.type() !== "checkbox") {
            return false;
        }
        return S25Util.toBool(this.value());
    });

    readonly validationErrors = computed(() =>
        WorkflowUtil.validateFieldReferences(
            this.value(),
            this.formCtx?.formFields() ?? [],
            this.acceptedAnswerTypes(),
            this.acceptedFieldTypes(),
        ),
    );
    answerTypeLabel(type: AnswerType) {
        return WorkflowUtil.answerTypeLabel(type);
    }

    fieldTypeLabel(fieldType: FieldType) {
        return WorkflowUtil.fieldTypeLabel(fieldType);
    }

    onCheckboxChange(event: Event) {
        const target = event.target as HTMLInputElement;
        this.value.set(target.checked ? "true" : "false");
    }
}
